import React, { useEffect } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { PhotoBackgroundLayout } from '../HeaderV2/layouts';
import { EventNameNavForStandaloneHeaderProps } from '../HeaderV2/components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { EventPageType } from '@graphql/generated';
import { GuestSiteEventDesign } from '../../../layout.types';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { VideoBackgroundHotfixLayout, VideoBackgroundLayout } from '../HeaderV2/layouts';
import { useEventInfo } from '@shared/utils/eventInfo';

type StandaloneWebsiteHeaderProps = Readonly<EventNameNavForStandaloneHeaderProps> &
  Readonly<{
    websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
  }>;

type VideoBackgroundPayload = Readonly<{
  [eventId: string]: {
    videoId?: string;
    mobileVideoId?: string;
    fallbackImageUrl?: string;
  };
}>;

const isPreview = isInIframe();

export const StandaloneWebsiteHeader = (props: StandaloneWebsiteHeaderProps) => {
  const { websiteHeader, ...restProps } = props;
  const { eventInfo } = useEventInfo();
  const [isVideoBackgroundEnabled, , , payload] = useIsFeatureEnabled('websiteVideoBackgroundEnabled');
  const videoBackgroundPayload = eventInfo?.eventId && payload ? (payload as VideoBackgroundPayload)[eventInfo.eventId] : null;

  const headerPhoto = websiteHeader.headerPhoto;
  const showPlaceholder = isPreview && !!headerPhoto.presentationLayout;

  useEffect(() => {
    if (headerPhoto.presentationLayout) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [headerPhoto.presentationLayout]);

  switch (headerPhoto.presentationLayout) {
    case 'photoBackground':
      // If video background is enabled and there is a video background payload, render the video background layout
      // Otherwise, render the photo background layout
      // We recently changed the feature flag to be enabled by default,
      // so we need to check if the video background payload is present
      return isVideoBackgroundEnabled && videoBackgroundPayload ? (
        <VideoBackgroundHotfixLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
          videoId={videoBackgroundPayload?.videoId || ''}
          mobileVideoId={videoBackgroundPayload.mobileVideoId}
          fallbackImageUrl={videoBackgroundPayload?.fallbackImageUrl || ''}
        />
      ) : (
        <PhotoBackgroundLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          {...restProps}
        />
      );
    case 'videoBackground':
      return (
        <VideoBackgroundLayout
          websiteHeader={{ ...websiteHeader, headerPhoto }}
          showPlaceholder={showPlaceholder}
          {...restProps}
        />
      );
    default:
      return null;
  }
};

StandaloneWebsiteHeader.displayName = 'StandaloneWebsiteHeader';
